import { requestIdpToken } from '@heimdall/client';
import { decodeJwtPayload, flog } from '@lseg-ciam/auth/utils';
import { html, LitElement, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Subscription } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { catchError, switchMap } from 'rxjs';
import { of } from 'rxjs';
import { rigSharedStyles } from './heimdall-rig-styles.js';

// Import the existing token bundle stream and freshness check function
import { idpTokenBundle$, isFreshTokenBundle } from '@lseg-ciam/auth/token';
import type { TokenBundleIdp, TokenSetIdp } from '@lseg-ciam/auth/token';

const defaultLinkUrl = `${globalThis.location.origin}/public/heimdall/link.html`;

@customElement('heimdall-rig-account-linking')
export class HeimdallRigAccountLinking extends LitElement {
  @state()
  _subscription: Subscription | null = null;

  @state()
  _idpTokenBundle: TokenBundleIdp | null = null;

  @state()
  _entraToken: TokenSetIdp | null = null;

  @state()
  _linkApiEndpoint =
    'https://api.ppe.refinitiv.com/iam/account-linking/v1/accounts';

  @state()
  _linkResult: unknown = null;

  get accountLinkingEnabled(): boolean {
    // Check token bundle freshness using the existing function
    if (!this._idpTokenBundle) return false;

    return isFreshTokenBundle(this._idpTokenBundle);
  }

  onSubscriptionRequestChanged(event: Event) {
    const target = event.target as HTMLInputElement;

    const { checked } = target;

    if (!checked) {
      this._subscription?.unsubscribe();
      this._subscription = null;
      this._idpTokenBundle = null;
      return;
    }

    this._subscription = idpTokenBundle$
      .pipe(flog('IDP token bundle on RIG', 'background: blue; color: white'))
      .subscribe((tokenBundle) => {
        this._idpTokenBundle = tokenBundle;
      });
  }

  onLinkApiEndpointChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this._linkApiEndpoint = target.value;
  }

  async onRequestEntraToken() {
    try {
      this._entraToken = await requestIdpToken(defaultLinkUrl);
      console.log('Entra token:', this._entraToken);
    } catch (error) {
      console.error('Failed to fetch Entra token:', error);
    }
  }

  onLinkAccount() {
    if (this._idpTokenBundle && this._entraToken) {
      const bearerToken = this._idpTokenBundle.tokenSet.access_token;
      const entraIdToken = this._entraToken.id_token;

      const body = JSON.stringify({
        entraIdToken,
      });

      const headers = {
        Authorization: `Bearer ${bearerToken}`,
        'Content-Type': 'application/json',
      };

      fromFetch(this._linkApiEndpoint, {
        method: 'PUT',
        headers,
        body,
      })
        .pipe(
          switchMap((response) => {
            if (!response.ok) {
              throw new Error(`Failed to link account: ${response.statusText}`);
            }
            return response.json();
          }),
          catchError((error) => {
            console.error('Error linking account:', error);
            return of({ error: error.message });
          })
        )
        .subscribe((result) => {
          this._linkResult = result;
          console.log('Link account result:', result);
        });
    }
  }

  render() {
    return html`
      <div class="app">
        <h2>Account Linking</h2>
        <div class="entitlement-status">
          <strong>Account Linking Status:</strong>
          <span
            class="status ${this.accountLinkingEnabled
              ? 'enabled'
              : 'disabled'}"
          >
            ${this.accountLinkingEnabled ? '✅ ENABLED' : '❌ DISABLED'}
          </span>
          ${this._idpTokenBundle
            ? html`<span class="details"
                >(Based on current IDP token
                bundle)${!isFreshTokenBundle(this._idpTokenBundle)
                  ? ' (token expired)'
                  : ''}</span
              >`
            : html`<span class="details"
                >(No IDP token bundle available)</span
              >`}
        </div>
        <ul>
          <li>
            <label>
              Subscribe to idpTokenBundle$ stream
              <input
                type="checkbox"
                .checked=${!!this._subscription}
                @change=${this.onSubscriptionRequestChanged}
              />
            </label>
          </li>
          <li>
            <label>
              Link API Endpoint
              <input
                type="text"
                @change=${this.onLinkApiEndpointChanged}
                .value=${this._linkApiEndpoint}
                placeholder="Link API Endpoint"
              />
            </label>
          </li>
          <li>
            <button
              @click=${this.onRequestEntraToken}
              ?disabled=${!this.accountLinkingEnabled}
            >
              Request Entra Token
            </button>
          </li>
          <li>
            <button
              @click=${this.onLinkAccount}
              ?disabled=${!this._idpTokenBundle || !this._entraToken}
            >
              🔗 Link Account
            </button>
          </li>
        </ul>
        <h2>Idp token bundle</h2>
        <pre>${JSON.stringify(this._idpTokenBundle, null, 2)}</pre>
        ${this._idpTokenBundle?.tokenSet.access_token
          ? html`<h3>Decoded Access Token</h3>
              <pre>
${JSON.stringify(
                  decodeJwtPayload(this._idpTokenBundle.tokenSet.access_token),
                  null,
                  2
                )}</pre
              >`
          : ''}
        ${this._idpTokenBundle?.tokenSet.id_token
          ? html`<h3>Decoded ID Token</h3>
              <pre>
${JSON.stringify(
                  decodeJwtPayload(this._idpTokenBundle.tokenSet.id_token),
                  null,
                  2
                )}</pre
              >`
          : ''}
        <h2>Entra token</h2>
        <pre>${JSON.stringify(this._entraToken, null, 2)}</pre>
        ${this._entraToken?.id_token
          ? html`<pre>
${JSON.stringify(decodeJwtPayload(this._entraToken.id_token), null, 2)}</pre
            >`
          : ''}
        <h2>Link result</h2>
        <pre>${JSON.stringify(this._linkResult, null, 2)}</pre>
      </div>
    `;
  }

  static styles = [
    rigSharedStyles,
    css`
      .entitlement-status {
        background: #f5f5f5;
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 12px;
        margin: 10px 0;
        font-family: monospace;
      }

      .status.enabled {
        color: #28a745;
        font-weight: bold;
      }

      .status.disabled {
        color: #dc3545;
        font-weight: bold;
      }

      .details {
        color: #6c757d;
        font-size: 0.9em;
        margin-left: 8px;
      }
    `,
  ];
}

declare global {
  interface HTMLElementTagNameMap {
    'heimdall-rig-account-linking': HeimdallRigAccountLinking;
  }
}
